import React, { useState, useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { ArrowLeft, DollarSign, ShoppingBag, Link2, Settings, Loader2 } from 'lucide-react';
import EarningsTab from '../components/profile/EarningsTab';
import OrdersTab from '../components/profile/OrdersTab';
import ReferralsTab from '../components/profile/ReferralsTab';
import SettingsTab from '../components/profile/SettingsTab';

// Base tabs - referrals tab is conditionally included based on brand feature flag
const BASE_TABS = [
  { id: 'earnings', label: 'Earnings', icon: DollarSign },
  { id: 'orders', label: 'Orders', icon: ShoppingBag },
  { id: 'referrals', label: 'Referrals', icon: Link2, featureFlag: 'referrals' },
  { id: 'settings', label: 'Settings', icon: Settings }
];

function ProfilePage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const brand = useBrand();
  const [activeTab, setActiveTab] = useState('earnings');

  // Filter tabs based on brand feature flags
  const tabs = useMemo(() => {
    return BASE_TABS.filter(tab => {
      // If tab has a feature flag requirement, check it
      if (tab.featureFlag === 'referrals') {
        return brand.features?.referrals?.showReferralTab !== false;
      }
      return true;
    });
  }, [brand.features?.referrals?.showReferralTab]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/studio-x7k9m2" replace />;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'earnings':
        return <EarningsTab />;
      case 'orders':
        return <OrdersTab />;
      case 'referrals':
        return <ReferralsTab />;
      case 'settings':
        return <SettingsTab user={user} />;
      default:
        return <EarningsTab />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/studio-x7k9m2"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 text-sm"
          >
            <ArrowLeft size={18} />
            Back to Studio
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">My Account</h1>
              <p className="text-slate-500 text-sm mt-1">
                {user?.attributes?.email || 'Welcome'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-slate-500 text-sm">
          <p>&copy; {brand.legal?.copyrightYear || '2025'} {brand.name}. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/terms" className="hover:text-slate-700">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
            <Link to="/faq" className="hover:text-slate-700">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ProfilePage;
